/**
 * Every word a person can read in the web app, in Canadian English. Pages
 * never contain literal copy, so wording is changed here and only here.
 */
export const copy = {
  appName: 'On Call',
  appSub: 'Hematology',

  /** `calendarShort` is what a phone shows on the Calendar tab; its name stays `calendar`. */
  nav: {
    schedule: 'Schedule',
    history: 'History',
    calendar: 'Add to calendar',
    calendarShort: 'Calendar',
    requests: 'Requests',
    admin: 'Admin',
    /** The Admin tab's name while its To do page lists anything; the tab shows the number beside "Admin". */
    adminWaiting: (n: number) => `Admin, ${String(n)} ${n === 1 ? 'thing' : 'things'} to do`,
  },
  signOut: 'Sign out',
  signedInAs: 'Signed in as',
  changePasswordLink: 'Change password',
  roles: { viewer: 'Viewer', physician: 'Physician', admin: 'Admin' } as const,

  signIn: {
    title: 'Sign in',
    intro: 'Use the email and password an admin gave you.',
    email: 'Email',
    password: 'Password',
    submit: 'Sign in',
    working: 'Signing in…',
    forgot: 'Forgot your password? Ask an admin to reset it. They will give you a temporary one to sign in with.',
  },

  changePassword: {
    title: 'Choose a new password',
    intro: 'You signed in with a temporary password. Choose your own before continuing. At least 10 characters; a short phrase is easiest to remember.',
    next: 'New password',
    confirm: 'New password again',
    mismatch: 'The two new passwords do not match.',
    submit: 'Save new password',
    working: 'Saving…',
    wrongTemporary: 'The temporary password is not right. Use the one an admin gave you, exactly as it was written.',
    /** More than 15 minutes after signing in with a temporary password: the server has signed the person out. */
    signInAgain: 'For your security, sign in again with your temporary password. More than 15 minutes have passed since you signed in with it.',
    signInAgainButton: 'Sign in again',
    /** The same page, opened by choice from the header rather than forced at first sign-in. */
    own: {
      title: 'Change your password',
      intro: 'At least 10 characters; a short phrase is easiest to remember. Any other device you are signed in on is signed out; this one stays signed in.',
      current: 'Current password',
      wrong: 'Your current password is not right.',
      done: 'Password changed. Any other device you were signed in on has been signed out.',
      back: 'Back to the schedule',
    },
  },

  schedule: {
    title: 'Schedule',
    loading: 'Loading the schedule…',
    empty: 'No schedule has been published yet.',
    emptyAdmin: 'No schedule exists yet. Build one from the Admin area once the roster is in.',
    draftBadge: 'Draft',
    savedBadge: 'Released',
    /** The physicians are the people on call in this schedule, not today's roster. */
    summary: (weeks: number, physicians: number) => `${String(weeks)} weeks · ${String(physicians)} ${physicians === 1 ? 'physician' : 'physicians'}`,
    savedOn: (date: string) => `released ${date}`,
    tabs: { weeks: 'Weeks', distribution: 'Distribution' },
    columns: { onCall: 'On call', days: 'Days', physician: 'Physician', notes: 'Notes' },
    /**
     * Admins only: who asked for what in each week, beside the weeks of a
     * draft, or of a released schedule while they edit it (owner, 24 October
     * 2026). "Dr. Chen › Vacation"; one awaiting approval says so.
     */
    requests: {
      header: 'Requests',
      kinds: { vacation: 'Vacation', preferred: 'Prefer call', avoid: 'Avoid call', loa: 'Leave' } as const,
      pending: '(awaiting approval)',
      /** Read out after the line of the physician on call that week, whose line is in bold. */
      onCall: 'on call this week',
      unavailable: 'Who requested what could not be loaded, so the Requests column is not shown. Reload the page to try again.',
    },
    unassigned: 'Unassigned',
    you: 'You',
    exportCsv: 'Export CSV',
    print: 'Print',
    legend: { weekend: 'Weekend', holiday: 'Holiday', covered: 'Covered by the week before', coveredMonday: 'Monday covered by the week before' },
    thisWeek: 'This week',
    showEarlier: (n: number) => `Show ${String(n)} earlier ${n === 1 ? 'week' : 'weeks'}`,
    hideEarlier: 'Hide earlier weeks',
    scrollHint: 'Scroll the table sideways to see every column.',
    kinds: { 'long-weekend': 'Long weekend', christmas: 'Christmas week', 'new-year': 'New Year week', regular: 'Regular' } as const,
    conflicts: {
      consecutive: 'Consecutive weeks',
      avoid: 'Over an avoid preference',
      vacation: 'On vacation',
      loa: 'On leave',
      'before-start': 'Before their start date',
      'after-end': 'After their last day',
      unassigned: 'Nobody available',
    } as Record<string, string>,
    manual: 'Set by admin',
    edit: {
      start: 'Edit this schedule',
      stop: 'Done editing',
      savedNote: 'Changes to a released schedule take effect for everyone as soon as they are made. Physicians whose weeks move are told in the app, on their Schedule page.',
      physicianLabel: (range: string) => `Physician for ${range}`,
      swap: 'Swap',
      swapWith: 'Swap with this week',
      swapCancel: 'Cancel the swap',
      swapping: (range: string) => `Swapping ${range}: press Swap on the other week, or press it again to cancel.`,
      hint: 'Change a physician from the dropdown, or press Swap on two weeks to exchange them.',
      controls: 'Change',
    },
    requestNotice: {
      open: (name: string, due: string) => `Requests are open for ${name}, due ${due}.`,
      none: 'You have not submitted any yet.',
      some: (n: number) => `You have submitted ${String(n)} so far.`,
      link: 'Open requests',
    },
    /** On the home page, once the admin has released the schedule after the one shown. */
    upcomingNotice: {
      text: (name: string, period: string) => `The next schedule, ${name} (${period}), is out.`,
      mine: 'See your weeks',
      open: 'Open it',
    },
    overridden: 'Special week set by admin',
    warnings: {
      unassigned: (n: number) => `${String(n)} ${n === 1 ? 'week has' : 'weeks have'} nobody available. An admin needs to assign ${n === 1 ? 'it' : 'them'} by hand.`,
      conflicts: (n: number) => `${String(n)} ${n === 1 ? 'week is' : 'weeks are'} flagged with a conflict.`,
      spread: 'Long weekends are not evenly spread in this schedule.',
    },
  },

  personal: {
    summary: 'At a glance',
    onCallNow: 'On call now',
    you: 'You',
    nobody: 'Nobody assigned',
    until: (day: string) => `until ${day}`,
    next: 'Your next call',
    none: 'Nothing scheduled yet',
    /** Followed by the schedule's name, as a link to it. */
    inSchedule: 'in',
    thisSchedule: 'This schedule',
    thisScheduleLine: (weeks: number, lw: number) => `${String(weeks)} ${weeks === 1 ? 'week' : 'weeks'} · ${String(lw)} long ${lw === 1 ? 'weekend' : 'weekends'}`,
    special: (n: number) => `${String(n)} Xmas/NY`,
  },

  /**
   * On a physician's home page when an admin has changed their weeks of a
   * released schedule (owner, 24 October 2026). Each line is the week's true
   * on-call range, then what changed: "Mon 16 Nov – Sun 22 Nov: now Omar
   * Saleh, no longer you".
   */
  weekChanges: {
    title: 'Your on-call weeks changed',
    lost: (name: string) => `now ${name}, no longer you`,
    lostToNobody: 'no longer you; nobody is assigned yet',
    gained: (name: string) => `now you (was ${name})`,
    gainedFromNobody: 'now you (nobody had it before)',
    by: (who: string, day: string) => `changed by ${who}, ${day}`,
    byUnknown: (day: string) => `changed ${day}`,
    note: 'The schedules and your next call already show these changes.',
    gotIt: 'Got it',
    saving: 'Saving…',
    /** Read by a screen reader where the notice was, once "Got it" has closed it. */
    dismissed: 'Got it. The notice about your changed weeks is closed.',
    loadFailed: 'Changes to your on-call weeks could not be checked just now. The schedule below is up to date; reload the page to check again.',
    seenFailed: 'That could not be saved, so this notice would show again. Press Got it again in a moment.',
  },

  distribution: {
    columns: {
      physician: 'Physician',
      weeks: 'Weeks',
      longWeekends: 'Long weekends',
      special: 'Xmas/NY',
      leave: 'Leave weeks',
      lw12: 'Long weekends incl. past year',
      lastSpecial: 'Last Xmas/NY',
    },
    never: 'Never',
    /** Beside a name, so a smaller share explains itself. */
    joined: (day: string) => `joined ${day}`,
    lastDay: (day: string) => `last day ${day}`,
    note: 'Fair share is calculated over the weeks each physician is available. Long weekends incl. past year counts the 12 months before this schedule, by the dates of the weeks worked, plus this schedule; it is what evens out long weekends. Christmas and New Year weeks rotate over years, longest wait first.',
  },

  requests: {
    title: 'Requests',
    intro: 'Your vacation weeks, and the weeks you would like or would rather not be on call, for coming schedules. An admin reviews them before they count.',
    empty: 'No request periods yet. An admin opens one before each schedule is built.',
    open: 'Open',
    closed: 'Closed',
    due: (date: string) => `Due ${date}`,
    /** "8 submitted · 5 approved · 2 awaiting approval · 1 declined", leaving out the parts that are none. */
    submittedDetail: (n: number, counts: { pending: number; approved: number; declined: number }) =>
      [
        `${String(n)} submitted`,
        counts.approved > 0 ? `${String(counts.approved)} approved` : null,
        counts.pending > 0 ? `${String(counts.pending)} awaiting approval` : null,
        counts.declined > 0 ? `${String(counts.declined)} declined` : null,
      ]
        .filter((x) => x !== null)
        .join(' · '),
    view: 'View',
    edit: 'Make requests',
    columns: { period: 'Period', due: 'Due', status: 'Status', mine: 'Your requests' },
    /**
     * "Nothing needed this time": an answer with no requests, so the admin
     * knows the physician has answered and is not waiting on them (owner,
     * 24 October 2026). `on` and `due` are days, day first.
     */
    nothingNeeded: {
      button: 'Nothing needed this time',
      /** The same button on a phone, where the bar is one short line. Its accessible name stays the full one. */
      buttonShort: 'Nothing needed',
      sending: 'Sending…',
      hint: 'Need nothing this time? Press Nothing needed this time, so the admin knows you have answered.',
      answered: (on: string, due: string) => `You told the admin you need nothing this time, ${on}. You can still make requests until ${due}.`,
      /** The bar's one line on a phone, and its notice once answered. */
      line: 'Answered: nothing needed',
      /** A closed period, read only. */
      closed: (on: string) => `You told the admin you needed nothing this time, ${on}.`,
      /** The Requests list's "Your requests" column. */
      list: 'Answered: nothing needed',
      /** The home page's notice while the period is open, in place of "You have not submitted any yet." */
      home: 'You told the admin you need nothing this time.',
    },
    notPhysician: 'Only physicians on the rotation make requests. This page shows the periods that are open.',
    period: {
      loading: 'Loading the period…',
      intro: (due: string) => `Press Vacation, Prefer call or Avoid call on the weeks you need; press again to clear. Submit by ${due}. You can change them until then; an admin approves them before they count.`,
      closedIntro: 'This period is closed. Ask an admin if you need a change.',
      /** For a physician who is also an admin (owner, 24 October 2026: they approve their own requests like anyone's). */
      adminNote: 'You are an admin: your requests also appear under Admin → Availability, where they are approved like everyone\u2019s.',
      /** The request screen for an account with no physician on the rotation: nothing of its own to show. */
      notPhysician: 'Only physicians on the rotation make requests, and this account is not linked to one. Ask an admin if it should be.',
      meaningsTitle: 'What each choice means',
      meanings: [
        { kind: 'vacation', text: 'You are away. You are not put on call that week unless nobody else at all can be.' },
        { kind: 'preferred', text: 'You would like to be on call that week. Given when nothing stronger prevents it.' },
        { kind: 'avoid', text: 'You would rather not be on call that week. Avoided when possible, but it can still happen.' },
      ] as const,
      limitsTitle: 'Limits for this period',
      limits: {
        vacation: 'Up to 4 vacation weeks, of which at most 1 long-weekend week.',
        /** Only for a period with a Christmas or New Year week (hasSpecialWeeks). */
        special: 'One of the Christmas and New Year weeks may also be vacation, on top of the 4.',
        preferAvoid: 'Up to 4 prefer-call weeks and up to 4 avoid-call weeks.',
        together: 'Vacation and avoid call together at most 6.',
        consecutive: 'Two prefer-call weeks in a row get only one of them, because nobody does two weeks running.',
        /** Only when an admin has entered at least one week for this physician in the period. */
        adminEntries: 'Weeks an admin entered for you do not count toward these limits.',
      },
      adminEntries: 'Weeks an admin has already entered for you are marked, and cannot be changed here. Ask an admin to change one.',
      declinedNote: 'A declined request does not count. Ask an admin if you would like to know why.',
      weekColumn: 'Week',
      kinds: { vacation: 'Vacation', preferred: 'Prefer call', avoid: 'Avoid call' } as const,
      kindsLong: { vacation: 'Vacation', preferred: 'Preferred', avoid: 'Avoid', loa: 'Leave' } as const,
      entry: {
        vacation: 'Vacation, entered by an admin',
        loa: 'Leave, entered by an admin',
        preferred: 'Prefer call, entered by an admin',
        avoid: 'Avoid call, entered by an admin',
      } as const,
      /** In place of the choices on a week an admin entered; the Notes badge names what was entered. */
      setByAdmin: 'Set by an admin',
      leaveUnsaved: 'You have choices that are not submitted yet. Leave this page and lose them?',
      /** `replaced`: an admin entered something for the same week, and the entry counts instead of the request, whatever its decision. */
      status: { pending: 'Awaiting approval', approved: 'Approved', declined: 'Declined', replaced: 'Replaced by an admin\u2019s entry' } as const,
      counts: {
        vacation: (n: number, max: number) => `Vacation ${String(n)} of ${String(max)}`,
        vacationLongWeekends: (n: number, max: number) => `Long-weekend vacation ${String(n)} of ${String(max)}`,
        vacationSpecial: (n: number, max: number) => `Christmas/New Year vacation ${String(n)} of ${String(max)}`,
        preferred: (n: number, max: number) => `Prefer call ${String(n)} of ${String(max)}`,
        avoid: (n: number, max: number) => `Avoid call ${String(n)} of ${String(max)}`,
        vacationPlusAvoid: (n: number, max: number) => `Vacation + avoid call ${String(n)} of ${String(max)}`,
      },
      problems: {
        'too-many-vacation': 'More than 4 vacation weeks (the Christmas or New Year week does not count).',
        'too-many-vacation-long-weekends': 'More than 1 long-weekend week as vacation.',
        'too-many-preferred': 'More than 4 prefer-call weeks.',
        'too-many-avoid': 'More than 4 avoid-call weeks.',
        'too-many-vacation-plus-avoid': 'Vacation and avoid call together are more than 6.',
        'both-special-weeks-vacation': 'Only one of the Christmas week and the New Year week can be vacation.',
        'unknown-week': 'A week outside the period.',
        'duplicate-week': 'The same week twice.',
      } as Record<string, string>,
      /** The same problems, worded for a period with no Christmas or New Year week, where that rule cannot apply. */
      problemsNoSpecialWeeks: {
        'too-many-vacation': 'More than 4 vacation weeks.',
      } as Record<string, string>,
      warnings: {
        'consecutive-preferred': 'Two prefer-call weeks in a row: only one of them will be assigned, because nobody does two weeks running.',
      } as Record<string, string>,
      submit: 'Submit requests',
      /** The same button on a phone, where the bar is one short line. Its accessible name stays the full one. */
      submitShort: 'Submit',
      submitting: 'Submitting…',
      saved: 'Requests submitted. An admin will review them.',
      unsaved: 'You have changes that are not submitted yet.',
      weekLabel: (range: string) => `Request for ${range}`,
      /** The bar at the bottom, folded to one line on a phone. */
      bar: {
        /** "Vacation 2 · Prefer 2 · Avoid 1 · 1 note · not submitted", leaving out the kinds with none. */
        summary: (n: { vacation: number; preferred: number; avoid: number; notes: number; unsaved: boolean }) => {
          const parts = [
            n.vacation > 0 ? `Vacation ${String(n.vacation)}` : null,
            n.preferred > 0 ? `Prefer ${String(n.preferred)}` : null,
            n.avoid > 0 ? `Avoid ${String(n.avoid)}` : null,
          ].filter((x) => x !== null);
          if (parts.length === 0) parts.push('No requests chosen');
          if (n.notes > 0) parts.push(`${String(n.notes)} ${n.notes === 1 ? 'note' : 'notes'}`);
          if (n.unsaved) parts.push('not submitted');
          return parts.join(' · ');
        },
        submitted: (summary: string) => `Submitted · ${summary}`,
        overLimit: 'Over a limit: show why',
        overLimitOpen: 'Over a limit',
        failed: 'Not submitted: show why',
        failedOpen: 'Not submitted',
      },
      /** A closed period, read only: the physician's own requests and the admin's entries. */
      readOnly: {
        label: (period: string) => `Your requests for ${period}`,
        columns: { week: 'Week', request: 'Request', status: 'Status' },
        none: 'You made no requests for this period.',
      },
    },
  },

  calendar: {
    title: 'Add to your calendar',
    intro: 'Put the on-call schedule in the calendar app on your phone or computer. It updates by itself when the schedule changes.',
    choose: 'Most physicians want My schedule. Add Full schedule as well only if you also want to see who is on call the other weeks; your own weeks then show in both.',
    feeds: {
      mine: { name: 'My schedule', about: 'Only the weeks you are on call.' },
      full: { name: 'Full schedule', about: 'Every week, with who is on call.' },
    },
    none: 'No link yet.',
    create: 'Create link',
    link: (name: string) => `${name} link`,
    copy: 'Copy link',
    copied: 'Link copied. Paste it into your calendar app.',
    copyFailed: 'Your browser did not allow copying. The link is selected: copy it from the box.',
    open: 'Open in calendar app',
    openHint: 'Opens the calendar app on an iPhone, iPad or Mac, and Outlook on some computers.',
    renew: 'Make a new link',
    turnOff: 'Turn off',
    confirmRenew: (name: string) => `Make a new ${name} link? The current link stops working at once, and any calendar using it stops updating until you add the new one.`,
    confirmTurnOff: (name: string) => `Turn off the ${name} link? Any calendar using it stops updating.`,
    renewed: 'New link made. The old one no longer works.',
    turnedOff: 'Link turned off.',
    working: 'Working…',
    privacyTitle: 'Keep your links to yourself',
    privacy: [
      'Anyone who has one of your links can see that calendar without signing in. The calendars show only who is on call, never vacations, leave or requests.',
      'If a link may have been shared, make a new one: the old one stops working at once.',
    ],
    howTitle: 'Adding it to your calendar',
    how: [
      { who: 'iPhone, iPad or Mac:', what: 'press "Open in calendar app" on that device, or add a subscribed calendar in the Calendar app and paste the link.' },
      { who: 'Google Calendar:', what: 'on a computer, add another calendar "From URL" and paste the link. It then shows on your phone as well.' },
      { who: 'Outlook:', what: 'add a calendar, choose to subscribe from the web, and paste the link.' },
    ],
    timing:
      'Your calendar app decides how often it checks for changes. Apple Calendar lets you choose; Google Calendar can take several hours, sometimes a day. For a last-minute change, the app itself is always current.',
    coverage: 'Only released schedules appear, from a year back onward. Each week is an all-day event marked free, so it does not block your calendar for meetings.',
  },

  history: {
    title: 'History',
    intro: 'Every released schedule: past, current and upcoming. Open one to see its weeks and distribution.',
    empty: 'No schedules have been released yet.',
    current: 'Current',
    /** A released schedule that starts after today. */
    upcoming: 'Upcoming',
    columns: { name: 'Schedule', period: 'Period', weeks: 'Weeks', saved: 'Released' },
  },

  admin: {
    title: 'Admin',
    sections: {
      todo: 'To do',
      build: 'Build new schedule',
      schedules: 'Schedules',
      distribution: 'Distribution',
      availability: 'Availability',
      physicians: 'Physicians',
      users: 'Users',
      audit: 'Audit log',
      export: 'Export',
    },
    /**
     * The Admin tab's first page: only what is waiting for an admin, each with
     * a link to where it is done (owner, 24 October 2026). Days are day
     * first, with the year only when it is not this year's.
     */
    todo: {
      title: 'To do',
      intro: 'What is waiting for you, each with a link to where it is done.',
      loading: 'Loading…',
      unavailable: 'The to-do list could not be loaded.',
      nothing: 'Nothing waiting.',
      nothingDetail: 'Requests to approve, answers still to come, drafts, the next schedule to build, flagged weeks and holiday warnings appear here when there are any.',
      /** A small label above each item, so the list can be scanned. */
      kinds: {
        'pending-requests': 'Requests',
        'not-answered': 'Answers',
        draft: 'Drafts',
        'next-schedule': 'Next schedule',
        'flagged-weeks': 'Flagged weeks',
        'holiday-check': 'Holiday dates',
      },
      pending: (n: number, period: string) => `${String(n)} ${n === 1 ? 'request' : 'requests'} for ${period} ${n === 1 ? 'is' : 'are'} awaiting approval.`,
      review: 'Review',
      reviewLabel: (period: string) => `Review the requests for ${period}`,
      notAnswered: (period: string, due: string) => `Not answered yet for ${period} (due ${due}):`,
      /** The signed-in admin's own name in that list. */
      you: (name: string) => `${name} (you)`,
      answerYourself: 'Make your requests',
      availability: 'Open Availability',
      availabilityLabel: (period: string) => `Open Availability for ${period}`,
      noAccount: (name: string) => `${name} has no account to answer with.`,
      deactivated: (name: string) => `${name}’s account is deactivated, so they cannot answer.`,
      users: 'Users',
      usersLabel: (name: string) => `Users, for ${name}`,
      /** The name is left out when it is only the dates again. */
      draft: (name: string, dates: string, made: string) => (name === dates ? `Draft ${dates}, made ${made}.` : `Draft ${name} (${dates}), made ${made}.`),
      open: 'Open',
      openLabel: (name: string) => `Open ${name}`,
      nextSchedule: (end: string) => `Released schedules end ${end}.`,
      nextScheduleEnded: (end: string) => `Released schedules ended ${end}.`,
      firstSchedule: 'No schedule has been released yet.',
      build: 'Build the next one',
      buildFirst: 'Build the first one',
      /** Only weeks whose on-call days are not over yet are counted. */
      flagged: (n: number, schedule: string) => `${String(n)} ${n === 1 ? 'week' : 'weeks'} still to come ${n === 1 ? 'is' : 'are'} flagged in ${schedule}.`,
      holidayDisagrees: 'canada-holidays.ca disagrees with this app about some holiday dates.',
      holidayFailing: 'The holiday dates could not be cross-checked against canada-holidays.ca for a while.',
      holidayLink: 'See the details',
      holidayLabel: 'See the holiday check on Build new schedule',
    },
    back: 'Back to schedule',
    users: {
      title: 'Users',
      intro: 'Everyone who can sign in. Admin is a grant on top of any account; a physician who is also an admin keeps their physician link.',
      add: 'Add a user',
      email: 'Email',
      displayName: 'Name',
      isAdmin: 'Admin privileges',
      physician: 'Linked physician',
      noPhysician: 'Not a physician',
      /** One account per physician (owner, 24 October 2026). */
      physicianNote: 'Each physician has one account. A physician who already has one is listed but cannot be chosen.',
      hasAccount: (name: string) => `${name} (has an account)`,
      /** The physician an account is linked to, after they were deactivated on the Physicians page. */
      deactivatedPhysician: (name: string) => `${name} (deactivated)`,
      create: 'Create account',
      creating: 'Creating…',
      created: (name: string) => `Account created for ${name}. Give them this temporary password now; it is shown only once.`,
      reset: 'Reset password',
      /** On the admin's own row, in place of Reset password, which would sign them out on the spot. */
      ownPassword: 'Change your own password',
      resetDone: (name: string) => `New temporary password for ${name}. Give it to them now; it is shown only once.`,
      deactivate: 'Deactivate',
      reactivate: 'Reactivate',
      grantAdmin: 'Make admin',
      revokeAdmin: 'Remove admin',
      columns: { name: 'Name', email: 'Email', role: 'Role', status: 'Status', lastSignIn: 'Last sign-in', actions: 'Actions' },
      status: { active: 'Active', inactive: 'Deactivated', pending: 'Temporary password' },
      never: 'Never',
      confirmDeactivate: (name: string) => `Deactivate ${name}? They will be signed out everywhere and cannot sign in until reactivated.`,
      confirmReset: (name: string) => `Reset the password for ${name}? Their current password stops working and they are signed out everywhere.`,
    },
    physicians: {
      title: 'Physicians',
      intro:
        'The roster. Leave the start date blank for anyone who was on the rotation before our records; give a newcomer their first day. A deactivate-on date is for a planned departure or retirement.',
      add: 'Add a physician',
      name: 'Name',
      initials: 'Initials',
      colour: 'Colour',
      startDate: 'Start date',
      endDate: 'Deactivate on',
      blank: 'Before our records',
      staying: 'Staying',
      create: 'Add to roster',
      creating: 'Adding…',
      edit: 'Edit',
      save: 'Save',
      cancel: 'Cancel',
      deactivate: 'Deactivate now',
      reactivate: 'Reactivate',
      columns: { name: 'Physician', dates: 'On the rotation', status: 'Status', actions: 'Actions' },
      status: { active: 'Active', inactive: 'Deactivated' },
      confirmDeactivate: (name: string) => `Deactivate ${name} now? They will not be assigned in new schedules. Released schedules keep their weeks.`,
      empty: 'No physicians yet. Add the roster before building a schedule.',
      /** Checked in the form before saving, and again by the server. */
      problems: {
        nameRequired: 'Enter the physician’s name.',
        initialsRequired: 'Enter the physician’s initials, up to 4 letters.',
        endBeforeStart: 'The deactivate-on date is before the start date. Choose a date on or after the start date, or leave it blank.',
      },
    },
    build: {
      title: 'Build a new schedule',
      intro: (start: string) => `The next schedule starts ${start}, the Monday after the last released one ends. Change the start or the length as needed.`,
      introNoHistory: 'Nothing has been released yet, so the start is the Monday after this week.',
      step1: 'Period',
      length: 'Length',
      months: (n: number) => `${String(n)} months`,
      custom: 'Custom range',
      start: 'Starts (snaps to a Monday)',
      end: 'Ends',
      result: 'Result',
      resultLine: (period: string, weeks: number) => `${period} · ${String(weeks)} weeks`,
      step2: 'Holidays in this period',
      civic: 'Count the Civic Holiday as a long weekend',
      civicNote: 'The group’s default is on. Changing it affects every schedule built from now on.',
      check: {
        agree: (when: string) => `The holiday dates agree with canada-holidays.ca (checked ${when}).`,
        disagree: 'canada-holidays.ca disagrees with this app about these holidays. The schedule follows this app’s own rules; check the Ontario government’s public holidays page, and if this app is wrong, the fix is a change to its rules.',
        date: (name: string, ours: string, theirs: string) => `${name}: this app has ${ours}, canada-holidays.ca has ${theirs}.`,
        observed: (name: string, ours: string, theirs: string) => `${name}: this app observes it on ${ours}, canada-holidays.ca on ${theirs}.`,
        unknownToUs: (name: string, date: string) => `${name} (${date}) is listed by canada-holidays.ca but not by this app.`,
        unknownToApi: (name: string, date: string) => `${name} (${date}) is in this app but not listed by canada-holidays.ca.`,
        never: 'The holiday dates have not yet been cross-checked against canada-holidays.ca; the check runs daily and the schedule does not depend on it.',
        stale: (when: string) => `The holiday dates could not be cross-checked against canada-holidays.ca for over a week (last success ${when}). The schedule does not depend on it.`,
        failure: {
          timeout: 'The last attempt timed out.',
          unreachable: 'The last attempt could not reach the site.',
          'unexpected-answer': 'The last attempt got an answer this app did not understand.',
        } as Record<string, string>,
        unavailable: 'The holiday cross-check status could not be loaded.',
      },
      noHolidays: 'No long weekends or special weeks fall in this period.',
      special: 'Christmas and New Year weeks',
      specialIntro:
        'By the group\u2019s rule, Christmas week is the week containing Boxing Day and New Year week is the week after it. Confirm or change them before generating; the rotation follows what you confirm.',
      christmasWeek: 'Christmas week',
      newYearWeek: 'New Year week',
      noSpecial: 'None',
      sameWeek: 'Christmas and New Year cannot be the same week.',
      specialAdjusted: 'Changed from the rule.',
      effects: {
        'long-weekend': (holiday: string, days: number) => `Long weekend · ${holiday} · ${String(days)}-day week`,
        christmas: 'Christmas week',
        'new-year': 'New Year week',
      },
      step3: 'Generate',
      generateNote: 'Generates a draft using the roster, the approved availability and the released schedules. Only admins can see the draft until you release it.',
      generate: 'Generate draft schedule',
      generating: 'Generating…',
      needRoster: 'Add at least two physicians before building a schedule.',
      draftsExist: (n: number) => `${String(n)} draft ${n === 1 ? 'schedule exists' : 'schedules exist'} already. You can open them from Schedules.`,
      /** A request period that starts on the chosen Monday, offered as a length (and chosen unless the admin picks another). */
      matchPeriod: (name: string, period: string, weeks: number) => `Match ${name}: ${period}, ${String(weeks)} weeks`,
      /**
       * Requests still awaiting approval in a period overlapping the schedule.
       * The generator uses approved availability only, so the admin is told
       * on Generate and on the draft. Also used by the draft toolbar.
       */
      pending: {
        text: (n: number, name: string) =>
          `${String(n)} ${n === 1 ? 'request' : 'requests'} for ${name} ${n === 1 ? 'is' : 'are'} still awaiting approval and will not be used.`,
        link: (n: number) => (n === 1 ? 'Review it' : 'Review them'),
        unavailable: 'Requests awaiting approval could not be checked. Any still waiting will not be used.',
        unavailableLink: 'Check Availability',
      },
    },
    schedules: {
      title: 'Schedules',
      intro: 'Drafts and released schedules. A draft can be regenerated or discarded and only admins see it; a released schedule is visible to everyone and counts for fairness.',
      empty: 'No schedules yet. Build one from Build new schedule.',
      columns: { name: 'Schedule', period: 'Period', status: 'Status', actions: 'Actions' },
      open: 'Open',
      discard: 'Discard',
      delete: 'Delete',
      confirmDiscard: (name: string) => `Discard the draft "${name}"? This cannot be undone.`,
      confirmDelete: (name: string) =>
        `Delete the released schedule "${name}"? It will no longer count toward anyone’s fairness history. This cannot be undone.`,
    },
    availability: {
      title: 'Availability',
      intro: 'Vacations, leaves of absence, preferred and avoid weeks. Open a request period so physicians can submit theirs, approve what comes in, and enter anything yourself. Only you can enter a leave. No reason is ever recorded.',
      periods: 'Request periods',
      openPeriod: 'Open a request period',
      periodName: 'Name',
      periodStart: 'Schedule starts (a Monday)',
      periodEnd: 'Schedule ends',
      periodDue: 'Requests due',
      create: 'Open new period',
      creating: 'Opening…',
      opened: (name: string) => `Opened ${name}. Physicians now see a notice on their Schedule page.`,
      /** The server refused a period over the weeks of another; the period is named, with its dates unless they are its name. */
      overlaps: (name: string, dates: string) =>
        `These dates overlap ${name === dates ? name : `${name} (${dates})`}. Start this period after that one ends, or change or delete that one first.`,
      noPeriods: 'No request periods yet.',
      columns: { period: 'Period', due: 'Due', submitted: 'Submitted', pending: 'Awaiting approval', actions: 'Actions' },
      submittedLine: (physicians: number, requests: number) => `${String(physicians)} ${physicians === 1 ? 'physician' : 'physicians'} · ${String(requests)} ${requests === 1 ? 'request' : 'requests'}`,
      review: 'Review',
      close: 'Close',
      confirmClose: (pending: number) =>
        pending === 1
          ? '1 request is still awaiting approval. It will not count until you approve it. Close anyway?'
          : `${String(pending)} requests are still awaiting approval. They will not count until you approve them. Close anyway?`,
      closedNotice: (name: string) => `Closed ${name}. Physicians can no longer make or change requests for it; everything already in it is kept.`,
      reopen: 'Reopen',
      reopenTitle: (name: string) => `Reopen ${name}`,
      reopenDue: 'New due date',
      reopenConfirm: 'Reopen period',
      reopening: 'Reopening…',
      reopenCancel: 'Cancel',
      reopenNote:
        'Every request already in this period is kept as it is, whether approved, declined or awaiting approval. Physicians see the notice again and can make or change requests until the new due date; anything they change goes back to awaiting approval.',
      reopenStarted: 'This period has already started, so it cannot be reopened for requests. Enter any change on the availability grid below.',
      reopenedNotice: (name: string, due: string) => `Reopened ${name}. Physicians can make or change requests until ${due}.`,
      remove: 'Delete',
      confirmDelete: (name: string) => `Delete the request period "${name}" and every request in it? Approved requests stop counting. This cannot be undone.`,
      deletedNotice: (name: string) => `Deleted ${name} and every request in it.`,
      reviewTitle: (name: string) => `Requests for ${name}`,
      approveAll: 'Approve all pending',
      approve: 'Approve',
      decline: 'Decline',
      undo: 'Undo',
      removeRequest: 'Remove',
      confirmRemoveRequest: (name: string, week: string) => `Remove ${name}\u2019s request for the week of ${week}? It disappears from their list too. This cannot be undone.`,
      noRequests: 'No requests yet.',
      reviewColumns: { physician: 'Physician', week: 'Week', kind: 'Request', status: 'Status', actions: 'Actions' },
      /** Beside the signed-in admin's own name, in the review list and the grid. */
      you: 'You',
      youName: (name: string) => `${name} (you)`,
      /** In the review list, under a request whose week also has an admin entry: the entry counts instead. */
      entryOver: (kind: string) => `Admin entry: ${kind}`,
      grid: 'Availability grid',
      gridIntro:
        'Press a cell to see what that week holds and change it. An admin entry counts as soon as it is entered and wins over a request for the same week; a request counts only once approved.',
      range: 'Weeks shown',
      rangeStart: 'From',
      rangeEnd: 'To',
      /** A cell's accessible name and tooltip: who, which week, and everything the week holds, with where it came from. */
      cellLabel: (physician: string, range: string, state: string) => `${physician}, ${range}: ${state}`,
      cellState: {
        none: 'No entry',
        entry: (kind: string) => `${kind}, admin entry`,
        request: (kind: string, status: string) => `${kind} request, ${status}`,
        status: { approved: 'approved', pending: 'awaiting approval', declined: 'declined' } as const,
      },
      legend: {
        vacation: 'Vacation',
        loa: 'Leave',
        preferred: 'Preferred',
        avoid: 'Avoid',
        pending: 'Request awaiting approval',
        request: 'Approved request',
        declined: 'Declined request',
        over: 'Admin entry over a request',
      },
      letters: { vacation: 'V', loa: 'L', preferred: 'P', avoid: 'A' } as const,
      noRoster: 'Add physicians first.',
      /** The window a grid cell opens: the week's request (approve or decline) and the admin entry (set or clear), kept apart. */
      editor: {
        title: (physician: string, range: string) => `${physician} · ${range}`,
        requestTitle: 'Request from the physician',
        requestTitleYours: 'Your request',
        noRequest: 'No request for this week.',
        approve: 'Approve request',
        decline: 'Decline request',
        replaced: 'The admin entry below counts instead of this request while it is there.',
        entryTitle: 'Admin entry',
        entryHint: 'Counts as soon as it is entered, with no approval. None removes only the admin entry: a request stays as it is.',
        none: 'None',
        confirmLeave: (physician: string, range: string) =>
          `Enter a leave of absence for ${physician}, ${range}? They will not be put on call that week, and the week no longer counts toward their fair share. No reason is recorded.`,
        saving: 'Saving…',
        close: 'Close',
      },
    },
    distribution: {
      title: 'Distribution',
      intro: 'Call over any range of dates, across every released schedule. Absolute counts, or weighted by the weeks each physician was available (leave, a later start or a retirement lower what could be expected of them).',
      range: 'Range',
      from: 'From',
      to: 'To',
      lastYear: 'Past 12 months',
      view: 'View',
      absolute: 'Absolute',
      weighted: 'Weighted',
      summary: (period: string, weeks: number) => `${period} · ${String(weeks)} scheduled ${weeks === 1 ? 'week' : 'weeks'}`,
      empty: 'No released schedule has weeks in this range.',
      columns: {
        physician: 'Physician',
        weeks: 'Weeks',
        longWeekends: 'Long weekends',
        special: 'Xmas/NY',
        available: 'Available',
        leave: 'Leave weeks',
        expected: 'Expected',
        difference: 'Ahead / behind',
        perAvailable: 'Per 10 available',
      },
      availableOf: (n: number, of: number) => `${String(n)} of ${String(of)}`,
      absoluteNote: 'Available: the scheduled weeks in the range the physician was on the rotation and not on leave.',
      weightedNote:
        'Expected: the scheduled weeks shared out in proportion to each physician\u2019s available weeks. Ahead / behind: weeks worked minus expected. Per 10 available: weeks worked for every ten available weeks.',
    },
    export: {
      title: 'Export',
      intro: 'Download everything the app holds as one JSON file. Keep a copy somewhere safe: it is the second backup, alongside the weekly database backup.',
      what: 'What the file contains',
      includes: [
        'The roster, with start and end dates',
        'Every released schedule and draft, week by week',
        'Vacations, leaves, preferred and avoid weeks',
        'Request periods and every request with its decision',
        'Every change made to a week of a released schedule: who had it before and after, who changed it and when',
        'Who answered each request period, and when, including "Nothing needed this time"',
        'Accounts: name, email and role, but never a password',
        'The audit log and the group settings',
      ],
      excludes: 'Passwords and sign-in sessions are never included.',
      sensitive: 'The file names physicians and their vacation and leave weeks. Treat it as you would the schedule itself.',
      download: 'Download JSON export',
      restore: 'To restore from this file, hand it to whoever maintains the app; there is no upload button on purpose, so a stray file can never overwrite live data.',
    },
    audit: {
      title: 'Audit log',
      intro: 'Every change anyone has made, most recent first. Passwords and session details are never recorded.',
      columns: { when: 'When', who: 'Who', what: 'What' },
      system: 'System',
      actions: {
        'user-created': 'Created an account',
        'user-updated': 'Changed an account',
        'password-reset': 'Reset a password',
        'password-changed': 'Changed their password',
        'sign-in': 'Signed in',
        'physician-created': 'Added a physician',
        'physician-updated': 'Changed a physician',
        'settings-updated': 'Changed a setting',
        'schedule-drafted': 'Generated a draft schedule',
        'schedule-regenerated': 'Regenerated a draft',
        'schedule-saved': 'Released a schedule',
        'schedule-deleted': 'Deleted a schedule',
        'week-reassigned': 'Reassigned a week',
        'weeks-swapped': 'Swapped two weeks',
        'week-kind-overridden': 'Changed a week’s type',
        'request-period-opened': 'Opened a request period',
        'request-period-changed': 'Changed a request period',
        'request-period-closed': 'Closed a request period',
        'request-period-reopened': 'Reopened a request period',
        'request-period-deleted': 'Deleted a request period',
        'requests-submitted': 'Submitted requests',
        'request-approved': 'Approved a request',
        'request-declined': 'Declined a request',
        'request-pending': 'Reset a request to pending',
        'request-removed': 'Removed a request',
        'requests-approved-all': 'Approved all pending requests',
        'availability-set': 'Set availability',
        'bootstrap-admin-created': 'Created the first admin at start-up',
        'history-loaded': 'Loaded previous schedules at start-up',
        'data-exported': 'Downloaded the JSON export',
        'calendar-link-made': 'Made a calendar link',
        'calendar-link-turned-off': 'Turned off a calendar link',
      } as Record<string, string>,
      empty: 'Nothing has been recorded yet.',
    },
    draft: {
      banner: 'This is a draft. Only admins can see it. Regenerate it or change weeks by hand, then release it when the group is happy with it.',
      regenerate: 'Regenerate',
      regenerating: 'Regenerating…',
      nameLabel: 'Schedule name',
      namePlaceholder: 'e.g. Summer and fall 2026',
      save: 'Release schedule',
      saving: 'Releasing…',
      saved: 'Released. Everyone can see it now.',
      /**
       * The window that asks before a draft is released (owner, 24 October
       * 2026): releasing shows it to everyone and makes it count for
       * fairness, which the old "Save to history" did not make clear.
       */
      confirm: {
        title: (name: string) => `Release ${name} to everyone?`,
        points: [
          'Every physician sees it on their Schedule page and in History, and calendar links include their weeks.',
          'It counts toward fairness for every schedule built after it.',
          'It is no longer a draft. Weeks can still be changed, but each change is shown to the physicians it moves.',
        ],
        pending: (n: number) =>
          `${String(n)} ${n === 1 ? 'request' : 'requests'} for this period ${n === 1 ? 'is' : 'are'} still awaiting approval and ${n === 1 ? 'was' : 'were'} not used.`,
        release: 'Release to everyone',
        keep: 'Keep as draft',
      },
      discard: 'Discard draft',
      /** Asked before regenerating a draft with weeks changed by hand: regenerating replaces them. */
      confirmRegenerate: (n: number) => `Regenerate replaces your ${String(n)} ${n === 1 ? 'change' : 'changes'} made by hand. Continue?`,
      /** Beside Regenerate afterwards: how many weeks now have a different physician. */
      regenerated: (n: number) => (n === 0 ? 'No weeks changed' : `${String(n)} ${n === 1 ? 'week' : 'weeks'} changed`),
    },
  },

  errors: {
    network: 'The server could not be reached. Check your connection and try again.',
    unexpected: 'Something went wrong on our side. Try again in a moment; if it keeps happening, tell an admin.',
  },
} as const;

export function formatDateTime(iso: string | null): string {
  if (!iso) return copy.admin.users.never;
  const d = new Date(iso);
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto',
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(d);
}
